<form [formGroup]="form" (submit)="submitForm()">
  <mat-card class="p-4 bg-grey-button form-verification-of-non-filing">
    <h2>Verification of Non-Filing</h2>

    <p class="form-verification-of-non-filing__subtitle">
      Provide information for household members who have income in 2025 but did not file a 2025 federal income tax return.
    </p>

    <section class="form-verification-of-non-filing__section">
      <h3>Wage Earners (Non-Filers)</h3>

      <p>Add each household member who had income but did not file taxes.</p>

      <button
        id="addWageEarnerButton"
        data-pw="add_wage_earner_button"
        data-qa="add_wage_earner_button"
        [attr.aria-label]="'Add Wage Earner'"
        [disabled]="isReadOnly"
        mat-stroked-button
        color="primary"
        type="button"
        class="mb-3"
        (click)="addWageEarner()">
        + Add Wage Earner
      </button>

      <div formArrayName="wageEarners">
        @for (earner of wageEarners.controls; track earner; let i = $index) {
          <div [formGroupName]="i" class="form-verification-of-non-filing__entry-card">
            <div class="edit-field">
              <label
                [for]="'wageEarnerHouseholdMemberNameInput_' + i"
                [attr.aria-label]="'Household Member Name'">
                Household Member Name
              </label>

              <input
                [id]="'wageEarnerHouseholdMemberNameInput_' + i"
                [attr.data-pw]="'wage_earner_household_member_name_input_' + i"
                [attr.data-qa]="'wage_earner_household_member_name_input_' + i"
                [attr.aria-label]="'Household Member Name'"
                matInput
                formControlName="householdMemberName" />
            </div>

            <div class="edit-field">
              <label
                [for]="'wageEarnerAmountEarnedInput_' + i"
                [attr.aria-label]="'Amount Earned in 2025'">
                Amount Earned in 2025
              </label>

              <input
                [id]="'wageEarnerAmountEarnedInput_' + i"
                [attr.data-pw]="'wage_earner_amount_earned_input_' + i"
                [attr.data-qa]="'wage_earner_amount_earned_input_' + i"
                [attr.aria-label]="'Amount Earned in 2025'"
                matInput
                type="number"
                formControlName="amountEarned" />
            </div>

            <div class="form-verification-of-non-filing__radio-question">
              <label for="irsIssued" [attr.aria-label]="'IRS W-2 or 1099 issued?'">
                IRS W-2/1099 Issued?
              </label>

              <mat-radio-group
                [id]="'wageEarnerIrsW21099IssuedRadioGroup_' + i"
                [attr.data-pw]="'wage_earner_irs_w2_1099_issued_radio_group_' + i"
                [attr.data-qa]="'wage_earner_irs_w2_1099_issued_radio_group_' + i"
                [attr.aria-label]="'IRS W-2 or 1099 issued?'"
                formControlName="irsW21099Issued">
                <mat-radio-button
                  [id]="'wageEarnerIrsW21099IssuedYes_' + i"
                  [attr.data-pw]="'wage_earner_irs_w2_1099_issued_yes_' + i"
                  [attr.data-qa]="'wage_earner_irs_w2_1099_issued_yes_' + i"
                  [attr.aria-label]="'Yes'"
                  [value]="true">
                  Yes
                </mat-radio-button>

                <mat-radio-button
                  [id]="'wageEarnerIrsW21099IssuedNo_' + i"
                  [attr.data-pw]="'wage_earner_irs_w2_1099_issued_no_' + i"
                  [attr.data-qa]="'wage_earner_irs_w2_1099_issued_no_' + i"
                  [attr.aria-label]="'No'"
                  [value]="false">
                  No
                </mat-radio-button>
              </mat-radio-group>
            </div>

            <div class="edit-field">
              <label
                [for]="'wageEarnerEmployerNameInput_' + i"
                [attr.aria-label]="'Employer Name'">
                Employer's Name
              </label>

              <input
                [id]="'wageEarnerEmployerNameInput_' + i"
                [attr.data-pw]="'wage_earner_employer_name_input_' + i"
                [attr.data-qa]="'wage_earner_employer_name_input_' + i"
                [attr.aria-label]="'Employer Name'"
                matInput
                formControlName="employerName" />
            </div>

            <button
              [id]="'removeWageEarnerButton_' + i"
              [attr.data-pw]="'remove_wage_earner_button_' + i"
              [attr.data-qa]="'remove_wage_earner_button_' + i"
              [attr.aria-label]="'Remove Wage Earner'"
              mat-button
              color="warn"
              type="button"
              (click)="removeWageEarner(i)">
              Remove
            </button>
          </div>
        }
      </div>

      @if (wageEarners.length > 0) {
        <table class="form-verification-of-non-filing__summary">
          <thead>
            <tr>
              <th>Household Member Name</th>
              <th>Amount Earned in 2025</th>
              <th>IRS W-2/1099 Issued?</th>
              <th>Employer's Name</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            @for (earner of wageEarners.getRawValue(); track earner.householdMemberName; let i = $index) {
              <tr>
                <td
                  [id]="'summaryWageEarnerHouseholdMemberName_' + i"
                  [attr.data-pw]="'summary_wage_earner_household_member_name_' + i"
                  [attr.data-qa]="'summary_wage_earner_household_member_name_' + i"
                  [attr.aria-label]="earner.householdMemberName">
                  {{ earner.householdMemberName }}
                </td>

                <td
                  [id]="'summaryWageEarnerAmountEarned_' + i"
                  [attr.data-pw]="'summary_wage_earner_amount_earned_' + i"
                  [attr.data-qa]="'summary_wage_earner_amount_earned_' + i"
                  [attr.aria-label]="earner.amountEarned">
                  {{ earner.amountEarned | currency }}
                </td>

                <td
                  [id]="'summaryWageEarnerIrsW21099Issued_' + i"
                  [attr.data-pw]="'summary_wage_earner_irs_w2_1099_issued_' + i"
                  [attr.data-qa]="'summary_wage_earner_irs_w2_1099_issued_' + i"
                  [attr.aria-label]="earner.irsW21099Issued ? 'Yes' : 'No'">
                  <span
                    class="status-pill"
                    [class.status-pill--yes]="earner.irsW21099Issued"
                    [class.status-pill--no]="earner.irsW21099Issued === false">
                    {{ earner.irsW21099Issued ? 'Yes' : 'No' }}
                  </span>
                </td>

                <td
                  [id]="'summaryWageEarnerEmployerName_' + i"
                  [attr.data-pw]="'summary_wage_earner_employer_name_' + i"
                  [attr.data-qa]="'summary_wage_earner_employer_name_' + i"
                  [attr.aria-label]="earner.employerName">
                  {{ earner.employerName }}
                </td>

                <td>
                  <button
                    [id]="'editWageEarnerButton_' + i"
                    [attr.data-pw]="'edit_wage_earner_button_' + i"
                    [attr.data-qa]="'edit_wage_earner_button_' + i"
                    [attr.aria-label]="'Edit Wage Earner'"
                    mat-button
                    color="primary"
                    type="button">
                    Edit
                  </button>

                  <button
                    [id]="'deleteWageEarnerButton_' + i"
                    [attr.data-pw]="'delete_wage_earner_button_' + i"
                    [attr.data-qa]="'delete_wage_earner_button_' + i"
                    [attr.aria-label]="'Delete Wage Earner'"
                    mat-icon-button
                    color="warn"
                    type="button"
                    (click)="removeWageEarner(i)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      }

      <div class="form-verification-of-non-filing__info-banner">
        Enter total amount earned in 2025 from all sources for each household member listed above.
      </div>
    </section>

    <section class="form-verification-of-non-filing__section">
      <h3>Certification</h3>

      <p>
        I certify that the household members listed above have income in 2025 but have not filed and are not required to
        file a 2025 federal income tax return.
      </p>

      <p>
        I also certify that all of the information provided on this form is complete and accurate.
      </p>

      <mat-checkbox
        id="certifiedCheckbox"
        data-pw="certified_checkbox"
        data-qa="certified_checkbox"
        [attr.aria-label]="'I certify that the information provided is complete and accurate.'"
        formControlName="certified">
        I certify that the information provided is complete and accurate.
      </mat-checkbox>
    </section>

    @if (!form.disabled) {
      <div class="button-row">
        <button
          id="cancelButton"
          data-pw="cancel_button"
          data-qa="cancel_button"
          [attr.aria-label]="'Cancel'"
          mat-stroked-button
          type="button"
          (click)="onBack()">
          Cancel
        </button>

        <button
          id="submitButton"
          data-pw="submit_button"
          data-qa="submit_button"
          [attr.aria-label]="'Submit'"
          mat-flat-button
          color="primary"
          type="submit"
          [disabled]="!isFormReady()">
          Submit
        </button>
      </div>
    }
  </mat-card>
</form>